import React, { useMemo, useCallback, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AppIcon from '../../assets/logo/colegio_aleman_small.png';
import packageJson from '../../../package.json';
import { useTranslation } from 'react-i18next';
import DialogProfile from './DialogProfile';
import { CalendarIcon } from 'views/Home/icons/CalendarIcon';
import { CertificateIcon } from 'views/Home/icons/CertificateIcon';
import { CircularIcon } from 'views/Home/icons/CircularIcon';
import { CoursesIcon } from 'views/Home/icons/CoursesIcon';
import { KardexIcon } from 'views/Home/icons/KardexIcon';
import { NotificationsIcon } from 'views/Home/icons/NotificationsIcon';
import { PermissionIcon } from 'views/Home/icons/PermissionIcon';
import { PhotoStudentIcon } from 'views/Home/icons/PhotoStudentIcon';
import { ReportIcon } from 'views/Home/icons/ReportIcon';
import { TicketIcon } from 'views/Home/icons/TicketIcon';
import { UsersIcon } from 'views/Home/icons/userIcon';
import ButtonCreateTicket from 'views/Ticket/components/form-ticket';
import { SeminarsIcon } from 'views/Home/icons/SeminarsIcon';
import type { User } from 'views/Home/constants';
import { ReportSemanalIcon } from 'views/Home/icons/ReportSemanalIcon';

const drawerWidth = 136;

interface LayoutProps {
  children: React.ReactNode;
  window?: () => Window;
}

interface NavItem {
  id: string;
  path: string;
  translationKey: string;
  icon: React.ReactNode;
  roles: User['rol'][];
  condition?: (user: User) => boolean;
  textVariant?: 'default' | 'nowrap';
  padding?: number;
}

// ─── Custom hook for user data ─────────────────────────────────────────────────

const useUser = () => {
  const [user] = useState<User | null>(() => {
    try {
      const userLocal = localStorage.getItem('user');
      if (userLocal) return JSON.parse(userLocal) as User;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
    return null;
  });

  return { user };
};

// ─── Navigation configuration ─────────────────────────────────────────────────

const createNavItems = (): NavItem[] => [
  {
    id: 'users',
    path: 'users',
    translationKey: 'modulo.usuarios',
    icon: <UsersIcon height={60} />,
    roles: ['ADMINISTRADOR'],
  },
  {
    id: 'courses',
    path: 'courses',
    translationKey: 'modulo.cursos',
    icon: <CoursesIcon height={60} />,
    roles: ['ADMINISTRADOR'],
  },
  {
    id: 'calendar',
    path: 'calendar',
    translationKey: 'modulo.calendario',
    icon: <CalendarIcon height={60} />,
    roles: ['MONITOR'],
  },
  {
    id: 'reporte-semanal',
    path: 'report-semanal',
    translationKey: 'modulo.reporteSemanal',
    icon: <ReportSemanalIcon height={45} />,
    roles: ['PROFESOR'],
    condition: (user) => user.claims?.tipoProfesor !== 'KINDER',
  },
  {
    id: 'course-book',
    path: 'course-book',
    translationKey: 'modulo.libroDeCurso',
    icon: <CoursesIcon height={60} />,
    roles: ['PROFESOR'],
    condition: (user) => user.claims?.tipoProfesor === 'KINDER',
  },
  {
    id: 'reports',
    path: 'reports',
    translationKey: 'modulo.reportes',
    icon: <ReportIcon height={60} />,
    roles: ['MONITOR', 'PROFESOR'],
  },
  {
    id: 'permission',
    path: 'permission',
    translationKey: 'modulo.permisos',
    icon: <PermissionIcon height={60} />,
    roles: ['MONITOR', 'PROFESOR'],
    condition: (user) =>
      user.claims?.jefeCurso === true ||
      user.rol === 'MONITOR' ||
      user.claims?.esSegundoProfesorKinder === true,
    textVariant: 'nowrap',
  },
  {
    id: 'kardex',
    path: 'kardex',
    translationKey: 'modulo.kardex',
    icon: <KardexIcon height={60} />,
    roles: ['MONITOR', 'PROFESOR'],
    textVariant: 'nowrap',
  },
  {
    id: 'notifications',
    path: 'notifications',
    translationKey: 'modulo.notificaciones',
    icon: <NotificationsIcon height={60} />,
    roles: ['MONITOR'],
  },
  {
    id: 'circulares',
    path: 'circulares',
    translationKey: 'modulo.circularesReglamentos',
    icon: <CircularIcon height={43} />,
    padding: 15,
    roles: ['MONITOR'],
  },
  {
    id: 'ticket',
    path: 'ticket',
    translationKey: 'modulo.tickets',
    icon: <TicketIcon height={43} />,
    padding: 15,
    roles: ['MONITOR', 'PROFESOR'],
  },
  {
    id: 'certificates',
    path: 'certificates',
    translationKey: 'modulo.certificados',
    icon: <CertificateIcon height={43} />,
    padding: 15,
    roles: ['MONITOR'],
  },
  {
    id: 'student-photos',
    path: 'student-photos',
    translationKey: 'modulo.fotosEstudiantes',
    icon: <PhotoStudentIcon height={43} />,
    padding: 15,
    roles: ['MONITOR'],
    textVariant: 'nowrap',
  },
  {
    id: 'seminars',
    path: 'seminars',
    translationKey: 'modulo.seminarios',
    icon: <SeminarsIcon height={43} />,
    padding: 15,
    roles: ['MONITOR'],
  },
];

// ─── NavItemComponent ──────────────────────────────────────────────────────────

interface NavItemProps {
  item: NavItem;
  isActive: boolean;
  onClick: (path: string) => void;
  locale: string;
}

const NavItemComponent: React.FC<NavItemProps> = React.memo(
  ({ item, isActive, onClick, locale }) => {
  const { t } = useTranslation();
    const handleClick = useCallback(() => {
      onClick(item.path);
    }, [onClick, item.path]);

    const shouldUseNoWrap = item.textVariant === 'nowrap' && locale === 'de';

    return (
      <ListItemButton
        onClick={handleClick}
        sx={{
          display: 'block',
          textAlign: 'center',
          paddingTop: item.padding ? `${item.padding}px` : '8px',
          paddingBottom: item.padding ? `${item.padding}px` : '8px',
          borderTop: isActive ? 'solid #b0ad9f 1px' : 'solid transparent 1px',
          borderBottom: isActive
            ? 'solid #b0ad9f 1px'
            : 'solid transparent 1px',
          backgroundColor: isActive ? '#fcea87' : 'transparent',
          transition: 'background-color 0.2s ease',
          '&:hover': { background: '#fcea87' },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 0,
            m: 0,
            pb: item.padding ? '10px' : 0,
          }}
        >
          {item.icon}
        </ListItemIcon>
        <Box sx={{ fontSize: 13 }}>
          <Typography variant="body2" noWrap={shouldUseNoWrap}>
            {t(item.translationKey)}
          </Typography>
        </Box>
      </ListItemButton>
    );
  },
);

NavItemComponent.displayName = 'NavItemComponent';

// ─── MainLayout ────────────────────────────────────────────────────────────────

const MainLayout: React.FC<LayoutProps> = ({ children, window }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  const { user } = useUser();
  const { t, i18n } = useTranslation();

  const visibleNavItems = useMemo(() => {
    if (!user) return [];
    const navItems = createNavItems();
    return navItems.filter((item) => {
      const hasRole = item.roles.includes(user.rol);
      const meetsCondition = !item.condition || item.condition(user);
      return hasRole && meetsCondition;
    });
  }, [user]);

  const handleDrawerToggle = useCallback(() => {
    if (isDesktop) {
      setDesktopOpen((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  }, [isDesktop]);

  const handleNavigation = useCallback(
    (page: string) => {
      void navigate(`/${page}`);
    },
    [navigate],
  );

  const handleHomeNavigation = useCallback(() => {
    void navigate('/');
  }, [navigate]);

  const isNavItemActive = useCallback(
    (path: string) => {
      return location.pathname.startsWith(`/${path}`);
    },
    [location.pathname],
  );

  const renderNavItems = useMemo(() => {
    return visibleNavItems.map((item) => (
      <NavItemComponent
        key={item.id}
        item={item}
        isActive={isNavItemActive(item.path)}
        onClick={handleNavigation}
        locale={i18n.language}
      />
    ));
  }, [visibleNavItems, isNavItemActive, handleNavigation, i18n.language]);

  const drawer = useMemo(
    () => (
      <Box
        sx={{
          height: 'calc(100% - 40px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '5px' },
        }}
      >
        <Box sx={{ ...theme.mixins.toolbar }} />
        <Divider />
        <List>{renderNavItems}</List>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '40px',
          }}
        >
          <Typography variant="body2" color="textSecondary" align="center">
            {t('version')} {packageJson.version}
          </Typography>
        </Box>
      </Box>
    ),
    [theme.mixins.toolbar, renderNavItems],
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const shouldShowTicketButton =
    user?.rol === 'PROFESOR' || user?.rol === 'MONITOR';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Button
              startIcon={
                <img
                  style={{ marginTop: '0px' }}
                  src={AppIcon}
                  alt="app icon"
                />
              }
              onClick={handleHomeNavigation}
            >
              {isDesktop && (
                <Typography>
                  {t('colegioAleman')}
                </Typography>
              )}
            </Button>
          </Box>
          <Box
            sx={{
              ml: 'auto',
              maxWidth: 'calc(100% - 150px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DialogProfile />
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        aria-label="navigation menu"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          slotProps={{
            paper: {
              sx: { width: drawerWidth, backgroundColor: 'primary.light' },
            },
          }}
          keepMounted
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          open={desktopOpen}
          slotProps={{
            paper: {
              sx: { width: drawerWidth, backgroundColor: 'primary.light' },
            },
          }}
          sx={{ display: { xs: 'none', md: 'block' } }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          maxWidth: 'calc(100vw - 10px)',
          maxHeight: 'calc(100vh - 50px)',
          ml: { xs: 0, md: desktopOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create('margin-left', {
            easing: desktopOpen
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.sharp,
            duration: desktopOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ ...theme.mixins.toolbar }} />
        {children}
        {shouldShowTicketButton && user && <ButtonCreateTicket user={user} />}
      </Box>
    </Box>
  );
};

export default MainLayout;
